import { writable, derived, get } from 'svelte/store'
import { storage } from '$lib/storage/StorageManager'
import type { Document, DocumentInfo } from '$lib/storage/types'
import type { ErrorState } from '$lib/stores/grammar'
import type { InlineView } from '$lib/stores/inlineView'
import modal from '$lib/stores/modalStore'
import { getFileSystemWatcher } from '$lib/services/FileSystemWatcher'
import { contentIndexStore } from '$lib/stores/contentIndexStore'

/**
 * File Tab Interface
 */
export interface FileTab {
  id: string
  title: string
  content: string
  isDirty: boolean
  isActive: boolean
  lastSaved?: Date
  showSaveStatus?: boolean  // Whether to show the "saved" message
  lastModified?: Date  // When content was last modified
  lastReloaded?: number  // Timestamp when file was reloaded from external changes
  metadata?: Document['metadata']
  // Store error and inline view states per tab
  errorState?: ErrorState
  inlineViews?: InlineView[]
  // Store undo/redo history per tab
  history?: {
    entries: Array<{ timestamp: number; value: string }>
    currentIndex: number
  }
}

/**
 * File Store State
 */
interface FileStoreState {
  tabs: Map<string, FileTab>
  activeTabId: string | null
  documents: DocumentInfo[]
  isLoading: boolean
  currentFolder?: string  // Track current working folder
  tabOrder: string[]  // Array of tab IDs in order
}

/**
 * FileStore: Manages open documents and tabs
 * Single Responsibility: Document and tab state management
 */
class FileStore {
  private store = writable<FileStoreState>({
    tabs: new Map(),
    activeTabId: null,
    documents: [],
    isLoading: false,
    tabOrder: []
  })

  // Auto-save timers for each document
  private autoSaveTimers = new Map<string, NodeJS.Timeout>()
  private saveStatusTimers = new Map<string, NodeJS.Timeout>()

  // Generation counter to cancel stale enrichment when vault changes
  private enrichmentGeneration = 0
  
  constructor() {
    
    // Listen for image file deletion events
    if (typeof window !== 'undefined') {
      window.addEventListener('image-file-deleted', ((event: CustomEvent) => {
        const { filePath } = event.detail
        this.closeImageTab(filePath)
      }) as EventListener)
    }
  }
  
  // Subscribe to store changes
  subscribe = this.store.subscribe
  
  /**
   * Close image viewer tab if open
   */
  private closeImageTab(imagePath: string): void {
    const state = get(this.store)
    
    // Check all tabs for image viewers showing this file
    state.tabs.forEach((tab, tabId) => {
      // Check if this is an image viewer tab for the deleted image
      if (tab.metadata?.isImage && 
          (tab.metadata?.imagePath === imagePath || 
           tab.metadata?.imagePath?.endsWith(imagePath) ||
           (tab.metadata?.imagePath && imagePath.endsWith(tab.metadata.imagePath)))) {
        // Close this tab without save confirmation
        this.store.update(state => {
          state.tabs.delete(tabId)
          const newTabs = new Map(state.tabs)
          
          // If this was the active tab, activate another
          if (state.activeTabId === tabId) {
            const tabIds = Array.from(state.tabs.keys())
            const closingTabIndex = tabIds.indexOf(tabId)
            const remainingTabs = Array.from(newTabs.keys())
            
            let newActiveId: string | null = null
            let foundNext = false
            
            // Find the next available tab in the original order
            for (let i = closingTabIndex + 1; i < tabIds.length; i++) {
              if (remainingTabs.includes(tabIds[i])) {
                newActiveId = tabIds[i]
                foundNext = true
                break
              }
            }
            
            // If no next tab found, look for previous tabs
            if (!foundNext) {
              for (let i = closingTabIndex - 1; i >= 0; i--) {
                if (remainingTabs.includes(tabIds[i])) {
                  newActiveId = tabIds[i]
                  break
                }
              }
            }
            
            // If still no tab found, just pick the first remaining tab
            if (!newActiveId && remainingTabs.length > 0) {
              newActiveId = remainingTabs[0]
            }
            
            // Set new active tab
            state.activeTabId = newActiveId
            if (newActiveId) {
              const activeTab = newTabs.get(newActiveId)
              if (activeTab) {
                activeTab.isActive = true
              }
            }
          }
          
          return {
            ...state,
            tabs: newTabs,
            activeTabId: state.activeTabId
          }
        })
      }
    })
  }
  
  /**
   * Load all documents list
   */
  async loadDocuments() {
    this.store.update(state => ({ ...state, isLoading: true }))

    try {
      const documents = await storage.list()
      this.store.update(state => {
        return {
          ...state,
          documents,
          isLoading: false
          // IMPORTANT: Do NOT modify tabs or activeTabId here
        }
      })

      // Fire-and-forget: enrich partial metadata in background
      if (documents.some(d => d.metadataStatus === 'partial')) {
        this.enrichDocuments().catch(err => {
          console.error('[fileStore] Background enrichment failed:', err)
        })
      }
    } catch (error) {
      console.error('Failed to load documents:', error)
      this.store.update(state => ({ ...state, isLoading: false }))
    }
  }

  /**
   * Enrich documents with full metadata (preview, tags, wordCount) in background.
   * Uses batchReadHead IPC in Electron, falls back to promisePool for web.
   * A generation counter ensures stale enrichments are abandoned when
   * loadDocuments is called again or documents are reset.
   */
  async enrichDocuments() {
    const currentGeneration = ++this.enrichmentGeneration

    const state = this.getState()
    const documents = state.documents

    // Only enrich partial documents
    const partialDocs = documents.filter(d => d.metadataStatus !== 'ready')
    if (partialDocs.length === 0) return

    const isElectron = typeof window !== 'undefined' && (window as any).api?.batchReadHead

    if (isElectron) {
      // Batch enrichment via IPC
      const BATCH_SIZE = 30
      for (let i = 0; i < partialDocs.length; i += BATCH_SIZE) {
        // Abort if vault switched or loadDocuments was called again
        if (this.enrichmentGeneration !== currentGeneration) return

        const batch = partialDocs.slice(i, i + BATCH_SIZE)
        const paths = batch.map(d => d.id.endsWith('.md') ? d.id : `${d.id}.md`)

        try {
          const result = await (window as any).api.batchReadHead(paths)
          if (!result.success || this.enrichmentGeneration !== currentGeneration) return

          this.store.update(state => {
            const updatedDocs = [...state.documents]

            for (const meta of result.results) {
              if (meta.error) continue

              const docPath = meta.path.replace(/\.md$/, '')
              const docIndex = updatedDocs.findIndex(d => d.id === docPath)
              if (docIndex === -1) continue

              updatedDocs[docIndex] = {
                ...updatedDocs[docIndex],
                preview: meta.preview || '',
                tags: meta.tags?.length > 0 ? meta.tags : updatedDocs[docIndex].tags,
                wordCount: meta.wordCount || 0,
                metadataStatus: 'ready'
              }
            }

            return { ...state, documents: updatedDocs }
          })
        } catch (err) {
          console.error('[fileStore] Batch enrichment failed:', err)
          // Continue with next batch — don't abort entire enrichment
        }
      }
    } else {
      // Fallback: use promisePool for individual reads (web environment)
      const { promisePool } = await import('$lib/utils/concurrency')
      const tasks = partialDocs.map(doc => async () => {
        if (this.enrichmentGeneration !== currentGeneration) return null

        try {
          const { storage } = await import('$lib/storage/StorageManager')
          const fullDoc = await storage.load(doc.id)
          return {
            id: doc.id,
            preview: fullDoc.content?.substring(0, 200) || '',
            tags: fullDoc.metadata?.tags || [],
            wordCount: fullDoc.content?.length || 0
          }
        } catch {
          return null
        }
      })

      const results = await promisePool(tasks, 5)
      if (this.enrichmentGeneration !== currentGeneration) return

      this.store.update(state => {
        const updatedDocs = [...state.documents]
        for (const result of results) {
          if (result.status !== 'fulfilled' || !result.value) continue
          const meta = result.value
          const idx = updatedDocs.findIndex(d => d.id === meta.id)
          if (idx === -1) continue
          updatedDocs[idx] = {
            ...updatedDocs[idx],
            preview: meta.preview,
            tags: meta.tags,
            wordCount: meta.wordCount,
            metadataStatus: 'ready'
          }
        }
        return { ...state, documents: updatedDocs }
      })
    }
  }
  
  /**
   * Set the current working folder
   */
  setCurrentFolder(folderPath?: string) {
    this.store.update(state => ({
      ...state,
      currentFolder: folderPath
    }))
  }
  
  /**
   * Get the current working folder
   */
  getCurrentFolder(): string | undefined {
    const state = get(this.store)
    return state.currentFolder
  }
  
  /**
   * Refresh cache - clear and reload all documents
   */
  async refreshCache() {
    console.log('Refreshing document cache...')

    // Cancel any in-flight enrichment before resetting documents
    this.enrichmentGeneration++

    // Clear ALL cached data including tabs that don't have unsaved changes
    this.store.update(state => {
      // Close tabs without unsaved changes
      const tabsToKeep = new Map<string, FileTab>()
      state.tabs.forEach((tab, id) => {
        if (tab.isDirty) {
          console.log(`[refreshCache] Keeping dirty tab: "${tab.title}"`)
          tabsToKeep.set(id, tab)
        } else {
          console.log(`[refreshCache] Removing clean tab: "${tab.title}"`)
        }
      })
      
      return {
        ...state,
        documents: [],
        tabs: tabsToKeep,
        activeTabId: tabsToKeep.size > 0 ? Array.from(tabsToKeep.keys())[0] : null,
        isLoading: true
      }
    })
    
    // Reload all documents from filesystem
    await this.loadDocuments()
    
    // Trigger file explorer refresh
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('file-explorer-refresh'))
    }
    
    console.log('Document cache refreshed')
  }
  
  /**
   * Add document to cache (used by FileExplorer when finding new files)
   */
  addDocumentToCache(doc: DocumentInfo) {
    this.store.update(state => {
      // Check if document already exists
      const existingIndex = state.documents.findIndex(d => d.id === doc.id)
      if (existingIndex >= 0) {
        // Update existing document
        const newDocs = [...state.documents]
        newDocs[existingIndex] = doc
        return { ...state, documents: newDocs }
      } else {
        // Add new document
        return { ...state, documents: [doc, ...state.documents] }
      }
    })
  }
  
  /**
   * Create a tab from fallback file (Safari/iOS)
   * @param data File data including content
   */
  async createTabFromFallback(data: {
    id: string
    title: string
    content: string
    path?: string
    isModified?: boolean
    isFallback?: boolean
  }): Promise<void> {
    const state = get(this.store)
    
    // Check if already open
    if (state.tabs.has(data.id)) {
      await this.setActiveTab(data.id)
      return
    }
    
    // Create new tab
    const tab: FileTab = {
      id: data.id,
      title: data.title,
      content: data.content,
      isDirty: data.isModified || false,
      isActive: true,
      lastSaved: new Date(),
      metadata: {
        wordCount: data.content.length,
        characterCount: data.content.length,
        isFallback: true
      },
      history: {
        entries: [{ timestamp: Date.now(), value: data.content }],
        currentIndex: 0
      }
    }
    
    // Add tab and set as active
    this.store.update(state => {
      // Deactivate all tabs
      state.tabs.forEach(t => t.isActive = false)
      
      // Add new tab
      state.tabs.set(data.id, tab)
      
      return {
        ...state,
        tabs: new Map(state.tabs),
        activeTabId: data.id
      }
    })
    
    // Save session state
    this.saveSessionState()
  }
  
  /**
   * Open a document in a new tab or activate existing tab
   * @param id Document ID
   * @param options Options for opening the file
   * @param options.activate Whether to activate the tab (default: true)
   */
  async openFile(id: string, options: { activate?: boolean } = {}): Promise<void> {
    const { activate = true } = options
    const state = get(this.store)

    // Normalize the ID for comparison (remove leading slashes, trailing .md)
    const normalizeId = (fileId: string) => {
      return fileId.replace(/^\/+/, '').replace(/\.md$/, '')
    }

    const normalizedRequestId = normalizeId(id)

    // Check all existing tabs for the same file
    for (const [tabId, tab] of state.tabs) {
      const normalizedTabId = normalizeId(tabId)

      // Direct match after normalization
      if (normalizedRequestId === normalizedTabId) {
        if (activate) {
          await this.setActiveTab(tabId)
        }
        return
      }

      // 경로가 다른 동명 파일은 별도 탭으로 열림
      // 정확한 경로 매칭만 체크 (Direct match after normalization 이미 위에서 처리함)
    }

    try {
      // Load document from storage
      const doc = await storage.load(id)

      // Check if this is an image file
      const isImage = /\.(png|jpg|jpeg|gif|svg|webp|bmp)$/i.test(id)

      // Create new tab with initial history
      // Always add the initial content to history for existing documents
      const tab: FileTab = {
        id: doc.id,
        title: doc.title,
        content: doc.content,
        isDirty: false,
        isActive: activate,
        lastSaved: doc.metadata.lastSaved || doc.updatedAt,
        metadata: isImage ? {
          ...doc.metadata,
          isImage: true,
          imagePath: id
        } : doc.metadata,
        history: {
          entries: [{ timestamp: Date.now(), value: doc.content }],
          currentIndex: 0
        }
      }
      
      // Add tab and optionally set as active
      this.store.update(state => {
        if (activate) {
          // Deactivate all tabs only if we're activating this one
          state.tabs.forEach(t => t.isActive = false)
        }
        
        // Add new tab
        state.tabs.set(id, tab)
        
        return {
          ...state,
          tabs: new Map(state.tabs),
          activeTabId: activate ? id : state.activeTabId
        }
      })
      
      // Restore posting prompt if exists in metadata
      if (doc.metadata?.postingPrompt || doc.metadata?.postingPromptModelId) {
        const { postingPromptStore } = await import('./postingPrompt')
        postingPromptStore.loadForTab(id)
        postingPromptStore.save(
          doc.metadata.postingPrompt || '',
          doc.metadata.postingPromptModelId
        )
      }
      
      // Save session state
      this.saveSessionState()
    } catch (error) {
      console.error('Failed to open file:', error)
    }
  }
  
  /**
   * Open an image file in a new tab
   * @param imagePath The path to the image file
   */
  async openImageFile(imagePath: string): Promise<void> {
    const state = get(this.store)
    
    // Check if already open
    if (state.tabs.has(imagePath)) {
      await this.setActiveTab(imagePath)
      return
    }
    
    // Extract filename for title
    const fileName = imagePath.split('/').pop() || 'Image'
    
    // Create new tab for image
    const tab: FileTab = {
      id: imagePath,
      title: fileName,
      content: '',  // Images don't have text content
      isDirty: false,
      isActive: true,
      metadata: {
        wordCount: 0,
        characterCount: 0,
        isImage: true,
        imagePath: imagePath
      }
    }
    
    // Update state
    this.store.update(state => {
      // Set all other tabs to inactive
      state.tabs.forEach((t) => {
        t.isActive = false
      })
      
      // Add new tab
      state.tabs.set(imagePath, tab)
      
      return {
        ...state,
        tabs: new Map(state.tabs),
        activeTabId: imagePath
      }
    })
    
    // Save session state
    this.saveSessionState()
  }
  
  /**
   * Close a tab
   */
  async closeTab(id: string): Promise<boolean> {
    const state = get(this.store)
    const tab = state.tabs.get(id)
    
    if (!tab) return true
    
    // Check if dirty
    if (tab.isDirty) {
      const result = await modal.saveConfirm(tab.title)
      
      if (result === 'save') {
        await this.saveTab(id)
      } else if (result === 'cancel') {
        return false
      }
      // If 'discard', continue with closing
    }
    
    // Cancel auto-save timer
    this.cancelAutoSave(id)
    
    // Clean up posting prompt for this tab
    const { postingPromptStore } = await import('./postingPrompt')
    postingPromptStore.removeForTab(id)
    
    // Remove tab
    this.store.update(state => {
      // Get all tab IDs in order before deletion
      const tabIds = Array.from(state.tabs.keys())
      const closingTabIndex = tabIds.indexOf(id)
      
      state.tabs.delete(id)
      const newTabs = new Map(state.tabs)
      
      // If this was the active tab, activate another
      let newActiveId = state.activeTabId
      if (state.activeTabId === id) {
        const remainingTabs = Array.from(newTabs.keys())
        
        if (remainingTabs.length > 0) {
          // Simple logic: 
          // 1. If not the last tab, activate the next one
          // 2. If the last tab, activate the previous one
          // 3. Use tab order from the original array
          
          // Find the next available tab in the original order
          let foundNext = false
          
          // First, try to find a tab that comes after the closing tab
          for (let i = closingTabIndex + 1; i < tabIds.length; i++) {
            if (remainingTabs.includes(tabIds[i])) {
              newActiveId = tabIds[i]
              foundNext = true
              break
            }
          }
          
          // If no next tab found, try to find a previous tab
          if (!foundNext && closingTabIndex > 0) {
            for (let i = closingTabIndex - 1; i >= 0; i--) {
              if (remainingTabs.includes(tabIds[i])) {
                newActiveId = tabIds[i]
                break
              }
            }
          }
          
          // Fallback to first remaining tab if nothing found
          if (newActiveId === id) {
            newActiveId = remainingTabs[0]
          }
        } else {
          newActiveId = null
        }
        
        // Activate the new tab
        if (newActiveId) {
          const newActiveTab = newTabs.get(newActiveId)
          if (newActiveTab) {
            newActiveTab.isActive = true
          }
        }
      }
      
      return {
        ...state,
        tabs: newTabs,
        activeTabId: newActiveId
      }
    })
    
    // Save session state
    this.saveSessionState()
    
    return true
  }
  
  /**
   * Set active tab
   */
  async setActiveTab(id: string) {
    this.store.update(state => {
      // Create new Map with updated tab objects for reactivity
      const newTabs = new Map<string, FileTab>()
      
      // Copy all tabs with updated isActive state
      state.tabs.forEach((tab, tabId) => {
        newTabs.set(tabId, {
          ...tab,
          isActive: tabId === id
        })
      })
      
      return {
        ...state,
        tabs: newTabs,
        activeTabId: id
      }
    })
    
    // Load posting prompt for the new active tab
    const { postingPromptStore } = await import('./postingPrompt')
    const state = get(this.store)
    const tab = state.tabs.get(id)
    
    if (tab) {
      postingPromptStore.loadForTab(id)
      // If tab has saved posting prompt in metadata, restore it
      if (tab.metadata?.postingPrompt !== undefined) {
        postingPromptStore.save(
          tab.metadata.postingPrompt as string || '',
          tab.metadata.postingPromptModelId as string
        )
      }
    }
  }
  
  /**
   * Save error and inline view state for a tab
   */
  saveTabCheckState(id: string, errorState: ErrorState, inlineViews: InlineView[]) {
    this.store.update(state => {
      const tab = state.tabs.get(id)
      if (!tab) return state
      
      // Create new tab object with updated state
      const updatedTab = {
        ...tab,
        errorState,
        inlineViews
      }
      
      // Create new Map with updated tab
      const newTabs = new Map(state.tabs)
      newTabs.set(id, updatedTab)
      
      return {
        ...state,
        tabs: newTabs
      }
    })
  }
  
  /**
   * Add to tab's undo history
   */
  addToHistory(id: string, content: string) {
    this.store.update(state => {
      const tab = state.tabs.get(id)
      if (!tab) return state
      
      const history = tab.history || {
        entries: [],
        currentIndex: -1
      }
      
      // Don't add duplicate entries
      const currentEntry = history.currentIndex >= 0 ? history.entries[history.currentIndex] : null
      if (currentEntry && currentEntry.value === content) {
        return state
      }
      
      // Special case: If history is empty, this is the first content
      if (history.entries.length === 0) {
        // Add as the initial history entry
        const updatedTab = {
          ...tab,
          history: {
            entries: [{ timestamp: Date.now(), value: content }],
            currentIndex: 0
          }
        }
        
        const newTabs = new Map(state.tabs)
        newTabs.set(id, updatedTab)
        
        return {
          ...state,
          tabs: newTabs
        }
      }
      
      // Remove entries after current index (for redo)
      // Keep all entries up to and including current index
      const newEntries = [...history.entries.slice(0, history.currentIndex + 1)]
      
      // Add new entry
      newEntries.push({
        timestamp: Date.now(),
        value: content
      })
      
      // Limit history size to 50 entries
      const maxHistorySize = 50
      if (newEntries.length > maxHistorySize) {
        newEntries.shift()
      }
      
      const updatedTab = {
        ...tab,
        history: {
          entries: newEntries,
          currentIndex: newEntries.length - 1
        }
      }
      
      const newTabs = new Map(state.tabs)
      newTabs.set(id, updatedTab)
      
      return {
        ...state,
        tabs: newTabs
      }
    })
  }
  
  /**
   * Undo for a specific tab
   */
  undo(id: string): string | null {
    const state = get(this.store)
    const tab = state.tabs.get(id)
    
    // Check if we have history and can undo
    if (!tab?.history || tab.history.entries.length === 0 || tab.history.currentIndex <= 0) {
      return null
    }
    
    const newIndex = tab.history.currentIndex - 1
    const previousContent = tab.history.entries[newIndex].value
    
    this.store.update(state => {
      const tab = state.tabs.get(id)
      if (!tab?.history) return state
      
      const updatedTab = {
        ...tab,
        content: previousContent,
        history: {
          ...tab.history,
          currentIndex: newIndex
        }
      }
      
      const newTabs = new Map(state.tabs)
      newTabs.set(id, updatedTab)
      
      return {
        ...state,
        tabs: newTabs
      }
    })
    
    return previousContent
  }
  
  /**
   * Redo for a specific tab
   */
  redo(id: string): string | null {
    const state = get(this.store)
    const tab = state.tabs.get(id)
    
    if (!tab?.history || tab.history.currentIndex >= tab.history.entries.length - 1) {
      return null
    }
    
    const newIndex = tab.history.currentIndex + 1
    const nextContent = tab.history.entries[newIndex].value
    
    this.store.update(state => {
      const tab = state.tabs.get(id)
      if (!tab?.history) return state
      
      const updatedTab = {
        ...tab,
        content: nextContent,
        history: {
          ...tab.history,
          currentIndex: newIndex
        }
      }
      
      const newTabs = new Map(state.tabs)
      newTabs.set(id, updatedTab)
      
      return {
        ...state,
        tabs: newTabs
      }
    })
    
    return nextContent
  }
  
  /**
   * Check if undo is available for a tab
   */
  canUndo(id: string): boolean {
    const state = get(this.store)
    const tab = state.tabs.get(id)
    // Can only undo if we have more than one entry AND we're not at the first entry
    return tab?.history ? tab.history.entries.length > 1 && tab.history.currentIndex > 0 : false
  }
  
  /**
   * Check if redo is available for a tab
   */
  canRedo(id: string): boolean {
    const state = get(this.store)
    const tab = state.tabs.get(id)
    return tab?.history ? tab.history.currentIndex < tab.history.entries.length - 1 : false
  }
  
  /**
   * Clear history for a tab - resets to current content only
   */
  clearHistory(id: string) {
    this.store.update(state => {
      const tab = state.tabs.get(id)
      if (!tab) return state
      
      // Reset history to just the current content
      const updatedTab = {
        ...tab,
        history: {
          entries: [{ timestamp: Date.now(), value: tab.content }],
          currentIndex: 0
        }
      }
      
      const newTabs = new Map(state.tabs)
      newTabs.set(id, updatedTab)
      
      return {
        ...state,
        tabs: newTabs
      }
    })
  }
  
  /**
   * Get saved check state for a tab
   */
  getTabCheckState(id: string): { errorState?: ErrorState; inlineViews?: InlineView[] } | null {
    const state = get(this.store)
    const tab = state.tabs.get(id)
    if (tab) {
      return {
        errorState: tab.errorState,
        inlineViews: tab.inlineViews
      }
    }
    return null
  }
  
  /**
   * Reload file content from disk (for external changes)
   */
  async reloadFile(id: string, force: boolean = false): Promise<void> {
    const state = get(this.store)
    const tab = state.tabs.get(id)
    if (!tab) return
    
    try {
      const oldContent = tab.content
      
      // Force reload document from storage, bypassing cache if requested
      const doc = await storage.load(id, force)
      
      // Always update the tab with fresh content
      const contentChanged = oldContent !== doc.content
      
      this.store.update(state => {
        const existingTab = state.tabs.get(id)
        if (existingTab) {
          const updatedTab = {
            ...existingTab,
            content: doc.content,
            isDirty: false, // External change, not dirty
            lastSaved: doc.updatedAt || Date.now(),
            // Force update timestamp to trigger reactivity
            lastReloaded: Date.now()
          }
          // Create new Map to ensure Svelte reactivity
          const newTabs = new Map(state.tabs)
          newTabs.set(id, updatedTab)
          
          return {
            ...state,
            tabs: newTabs
          }
        }
        
        return state
      })
      
      // Content change is handled silently
    } catch (error) {
      console.error(`[fileStore] Failed to reload file ${id}:`, error)
    }
  }
  
  /**
   * Reload all open files (for external changes)
   */
  async reloadAllFiles(force: boolean = false): Promise<void> {
    const state = get(this.store)
    const reloadPromises = Array.from(state.tabs.keys()).map(id => this.reloadFile(id, force))
    await Promise.all(reloadPromises)
  }
  
  /**
   * Update tab content
   */
  updateTabContent(id: string, content: string) {
    this.store.update(state => {
      const existingTab = state.tabs.get(id)
      if (existingTab) {
        // Create a new tab object to ensure reactivity
        const updatedTab = {
          ...existingTab,
          content,
          isDirty: true,
          lastModified: new Date()  // Track when content was last modified
        }
        state.tabs.set(id, updatedTab)
      }
      
      return {
        ...state,
        tabs: new Map(state.tabs)
      }
    })
    
    // Schedule auto-save with debounce
    this.scheduleAutoSave(id)
  }
  
  /**
   * Update tab title
   */
  updateTabTitle(id: string, title: string) {
    this.store.update(state => {
      const existingTab = state.tabs.get(id)
      if (existingTab) {
        // Create a new tab object to ensure reactivity
        const updatedTab = {
          ...existingTab,
          title,
          isDirty: true,
          lastModified: new Date()  // Track when title was last modified
        }
        state.tabs.set(id, updatedTab)
        
        // Also update in documents list
        const docIndex = state.documents.findIndex(doc => doc.id === id)
        if (docIndex !== -1) {
          state.documents[docIndex].title = title
        }
      }
      
      return {
        ...state,
        tabs: new Map(state.tabs),
        documents: [...state.documents]
      }
    })
    
    // Schedule auto-save with debounce
    this.scheduleAutoSave(id)
  }
  
  /**
   * Get current state
   */
  getState() {
    return get(this.store)
  }
  
  /**
   * Update tab path (for file rename)
   */
  updateTabPath(oldPath: string, newPath: string) {
    this.store.update(state => {
      // Try to find the tab with exact path first
      let tab = state.tabs.get(oldPath)
      let actualOldPath = oldPath

      // If not found, try to find by normalized path (without .md extension)
      if (!tab) {
        const normalizeId = (id: string) => id.replace(/^\/+/, '').replace(/\.md$/, '')
        const normalizedOldPath = normalizeId(oldPath)

        // Search through all tabs for a matching normalized ID
        for (const [tabId, tabData] of state.tabs) {
          if (normalizeId(tabId) === normalizedOldPath) {
            tab = tabData
            actualOldPath = tabId
            break
          }
        }
      }

      if (tab) {
        // Create new tabs map
        const newTabs = new Map(state.tabs)

        // Update tab ID
        tab.id = newPath

        // Add new entry BEFORE removing old one to prevent derived stores from becoming null
        newTabs.set(newPath, tab)
        // Now remove old entry
        newTabs.delete(actualOldPath)

        // Update in documents list
        const newDocuments = [...state.documents]
        const docIndex = newDocuments.findIndex(doc =>
          doc.id === actualOldPath || doc.id === oldPath
        )
        if (docIndex !== -1) {
          newDocuments[docIndex] = { ...newDocuments[docIndex], id: newPath }
        }

        // Update active tab if it was the renamed tab - IMPORTANT for file explorer
        let newActiveTabId = state.activeTabId
        if (state.activeTabId === actualOldPath || state.activeTabId === oldPath) {
          newActiveTabId = newPath
        }

        // Update tab order if it exists
        let newTabOrder = state.tabOrder ? [...state.tabOrder] : undefined
        if (newTabOrder) {
          const orderIndex = newTabOrder.indexOf(actualOldPath)
          if (orderIndex !== -1) {
            newTabOrder[orderIndex] = newPath
          } else {
            // Try to find with original oldPath
            const altOrderIndex = newTabOrder.indexOf(oldPath)
            if (altOrderIndex !== -1) {
              newTabOrder[altOrderIndex] = newPath
            }
          }
        }

        return {
          ...state,
          tabs: newTabs,
          documents: newDocuments,
          activeTabId: newActiveTabId,
          ...(newTabOrder ? { tabOrder: newTabOrder } : {})
        }
      } else {
        console.warn(`[updateTabPath] Tab not found for oldPath: ${oldPath}`)
      }

      return state
    })
  }
  
  /**
   * Generate unique title by appending number if needed
   * @param baseTitle - Base title to use
   * @param folderPath - Optional folder path to check within (null for root)
   */
  async generateUniqueTitle(baseTitle: string = 'Untitled', folderPath?: string): Promise<string> {
    const base = baseTitle  // Define base from baseTitle parameter
    const state = get(this.store)
    const existingTitles = new Set<string>()
    
    // Check filesystem within the specific folder only
    try {
      const { vaultStore } = await import('./vaultStore')
      // IMPORTANT: Pass undefined or empty string for root, not false
      const files = await vaultStore.listFiles(folderPath || '', false) // non-recursive to get only files in target folder
      
      files.forEach(file => {
        // IMPORTANT: Only check files, not directories
        if (file.type === 'file' && file.name.endsWith('.md')) {
          // Skip template/prompt files in prompts directory
          if (file.path && (file.path.includes('prompts/') || file.path.includes('templates/'))) {
            return
          }
          
          const title = file.name.replace('.md', '')
          existingTitles.add(title.toLowerCase())
        }
      })
      
      // Also check open tabs in the same folder
      // Need to extract relative folder path from tab.id
      const { vaultStore: vs } = await import('./vaultStore')
      const vault = get(vs)
      const vaultPath = vault?.vaultPath || ''

      // Defensive check: warn if vault path not available in Electron
      const isElectron = typeof window !== 'undefined' && (window as any).api
      if (isElectron && !vaultPath) {
        console.warn('[fileStore.generateUniqueTitle] Vault path not available yet, tab folder comparison may be inaccurate')
      }

      state.tabs.forEach(tab => {
        let tabFolder = ''

        // Extract folder from tab id
        if (tab.id.includes('/')) {
          const fullPath = tab.id.substring(0, tab.id.lastIndexOf('/'))

          // Make it relative to vault path if necessary
          if (vaultPath && fullPath.startsWith(vaultPath)) {
            tabFolder = fullPath.substring(vaultPath.length).replace(/^\//, '')
          } else if (fullPath.startsWith('/')) {
            // Already absolute, but not vault path - might be the relative part
            tabFolder = fullPath.replace(/^\//, '')
          } else {
            tabFolder = fullPath
          }
        }
        
        // Compare relative paths
        if (tabFolder === (folderPath || '')) {
          existingTitles.add(tab.title.toLowerCase())
        }
      })
    } catch (err) {
      console.error('Could not check file system for unique title:', err)
    }
    
    // If base title doesn't exist, use it
    if (!existingTitles.has(base.toLowerCase())) {
      return base
    }
    
    // Find the next available number (start from 1, not 2)
    let counter = 1
    while (existingTitles.has(`${base} ${counter}`.toLowerCase())) {
      counter++
    }
    
    return `${base} ${counter}`
  }
  
  /**
   * Save tab to storage
   */
  async saveTab(id: string, newTitle?: string): Promise<void> {
    const state = get(this.store)
    const originalTab = state.tabs.get(id)

    if (!originalTab) return

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('flush-editor-content', {
          detail: { tabId: id }
        })
      )
    }

    // If new title is provided, update tab object directly (don't call updateTabTitle yet)
    let tab: FileTab = newTitle && newTitle !== originalTab.title
      ? { ...originalTab, title: newTitle }
      : originalTab

    try {
      // Store the original tab ID (this is what the tab is keyed by)
      const tabId = id
      
      // Check if we need to rename the file based on title change
      let fileId = id
      
      // Ensure ID has .md extension if it doesn't already
      if (!fileId.endsWith('.md')) {
        fileId = fileId + '.md'
      }
      
      // Store original file ID to track the actual file location
      const originalFileId = fileId
      
      // Remove vault path if present to get relative path for storage operations
      const { vaultStore: vs } = await import('./vaultStore')
      const vault = get(vs)
      let relativeFileId = fileId

      // Defensive check: warn if vault path not available in Electron
      const isElectronSave = typeof window !== 'undefined' && (window as any).api
      if (isElectronSave && !vault?.vaultPath) {
        console.warn('[fileStore.saveTab] Vault path not available yet, using file ID as-is:', fileId)
      }

      if (vault?.vaultPath && fileId.startsWith(vault.vaultPath)) {
        relativeFileId = fileId.substring(vault.vaultPath.length).replace(/^\//, '')
      }

      // Use relative path for all operations
      fileId = relativeFileId
      
      // Check if we renamed the file (will be set to true if filename changes)
      let wasRenamed = false
      
      // Handle file renaming based on title change (for all files)
      if (fileId.endsWith('.md')) {
        // Extract current filename and check if it differs from title
        const lastSlash = fileId.lastIndexOf('/')
        const lastDot = fileId.lastIndexOf('.')
        
        // Handle both with path (folder/file.md) and without path (file.md)
        const currentFileName = lastDot !== -1
          ? fileId.substring(lastSlash + 1, lastDot)
          : fileId.substring(lastSlash + 1)
        
        // Sanitize title for filename (keep spaces)
        const sanitizedTitle = tab.title
          .replace(/[<>:"/\\|?*\x00-\x1f]/g, '') // Remove invalid filesystem characters and control chars
          .trim() // Remove leading/trailing spaces
          .slice(0, 100) // Limit length

        // If filename needs to change, handle it more gracefully
        if (currentFileName !== sanitizedTitle && sanitizedTitle) {
          // Get directory (empty string if no path)
          const directory = lastSlash !== -1 ? fileId.substring(0, lastSlash + 1) : ''
          const extension = lastDot !== -1 ? fileId.substring(lastDot) : '.md'

          // Generate unique filename if needed
          let basePath = directory + sanitizedTitle
          let newPath = basePath + extension
          let counter = 2

          // Normalize paths for comparison (handle extension differences)
          const normalizePathForCompare = (path: string) => {
            return path.replace(/\.md$/i, '').toLowerCase()
          }
          const normalizedFileId = normalizePathForCompare(fileId)

          // Check for existing files - compare with fileId (the current path with .md)
          const existingPaths = new Set(state.documents.map(doc => doc.id))
          while (existingPaths.has(newPath) && normalizePathForCompare(newPath) !== normalizedFileId) {
            newPath = `${basePath} ${counter}${extension}`
            counter++
          }

          // Only proceed if path actually changed (use normalized comparison)
          if (normalizePathForCompare(newPath) !== normalizedFileId) {
            // Tab is open, so file should exist (unless it's a new unsaved file)
            // Check if this is a new file by checking if it has lastSaved date
            const isNewFile = !tab.lastSaved

            if (!isNewFile) {
              // Existing file - perform rename
              try {
                const { vaultStore } = await import('./vaultStore')

                // Request permission BEFORE attempting rename (for web browsers)
                // This ensures File System Access API has valid permissions
                const isElectron = typeof window !== 'undefined' && (window as any).api
                if (!isElectron) {
                  const permissionGranted = await vaultStore.requestPermission()
                  if (!permissionGranted) {
                    throw new Error('파일 이름 변경 권한이 필요합니다.')
                  }
                }

                // Mark both old and new paths as self-change BEFORE renaming
                const fsWatcher = getFileSystemWatcher()
                const oldFilePath = fileId.endsWith('.md') ? fileId : `${fileId}.md`
                const newFilePath = newPath.endsWith('.md') ? newPath : `${newPath}.md`
                fsWatcher.markSelfChange(oldFilePath)
                fsWatcher.markSelfChange(newFilePath)

                // IMPORTANT: Rename BEFORE updating tab path
                await vaultStore.renameFile(fileId, newPath)

                // Update storage's documents Map to reflect the rename
                if (storage && typeof (storage as any).rename === 'function') {
                  (storage as any).rename(fileId, newPath, tab.title)
                }

                // Only update paths after successful rename
                // Don't call updateTabPath here - we'll update everything in one go later
                const originalFileId = fileId
                fileId = newPath
                wasRenamed = true

                // Update tab object with new id
                tab = { ...tab, id: newPath }

              } catch (error) {
                console.error('[saveTab] Rename failed:', error)
                // Rename failed - keep original filename
                // DON'T create new file - just keep the original path
                // The file will be saved with original name
              }
            } else {
              // New file - just update the path for the initial save
              // Don't call updateTabPath here - we'll update everything in one go later
              fileId = newPath
              wasRenamed = true

              // Update tab object with new id
              tab = { ...tab, id: newPath }
            }
            
            // Document will be updated in the final store.update at the end of saveTab
          }
        }
      }

      // Store the ORIGINAL relative file ID (before any rename)
      // This is used for the file-renamed event to tell FileExplorer which node to update
      const originalRelativeFileId = originalFileId
      
      // Get posting prompt data for this tab
      const { postingPromptStore } = await import('./postingPrompt')
      const postingPromptData = postingPromptStore.get()
      
      // Check if file was renamed
      
      // If file was renamed, no need to save content again
      // The rename operation already moved the file with its content
      if (!wasRenamed) {
        // Mark this as a self-change to prevent showing modal
        const fsWatcher = getFileSystemWatcher()
        const filePath = fileId.endsWith('.md') ? fileId : `${fileId}.md`
        fsWatcher.markSelfChange(filePath)
        
        await storage.save(fileId, tab.content, {
          ...tab.metadata,
          title: tab.title, // Keep original title with spaces
          postingPrompt: postingPromptData?.prompt || '',
          postingPromptModelId: postingPromptData?.modelId
        })
      }
      
      // Update content index immediately after save
      try {
        // Get vault path to construct full path for content index
        const { vaultStore: vs } = await import('./vaultStore')
        const vault = get(vs)
        // Use relative path for content index (fileId is already relative)
        const relativePath = fileId.endsWith('.md') ? fileId : `${fileId}.md`
        
        await contentIndexStore.updateSingleDocument(relativePath, tab.content)
      } catch (err) {
        console.error('[FileStore] Failed to update content index:', err)
        // Don't throw - this shouldn't prevent the save from succeeding
      }
      
      // Update tab state AND documents list in SINGLE atomic update
      // This prevents multiple renders and derived store recalculations
      this.store.update(state => {
        // Use the original tabId to find the existing tab
        let existingTab = state.tabs.get(tabId)

        // If tab not found with exact key, try normalized search
        if (!existingTab) {
          const normalizeId = (id: string) => id.replace(/^\/+/, '').replace(/\.md$/, '')
          const normalizedTabId = normalizeId(tabId)

          for (const [id, t] of state.tabs) {
            if (normalizeId(id) === normalizedTabId) {
              existingTab = t
              break
            }
          }
        }

        // Determine what actually changed to minimize Map/object creation
        const titleChanged = existingTab && tab.title !== existingTab.title
        const idChanged = wasRenamed

        // Create result state - only copy tabs Map if we're modifying it
        let result = {
          ...state,
          tabs: (idChanged || titleChanged) ? new Map(state.tabs) : state.tabs,
          ...(state.tabOrder && wasRenamed ? { tabOrder: [...state.tabOrder] } : {})
        }

        if (existingTab) {
          const targetId = wasRenamed ? fileId : tabId
          const updatedTab: FileTab = {
            ...existingTab,
            ...tab,
            id: targetId,
            isDirty: false,
            lastSaved: new Date(),
            showSaveStatus: true
          }

          const newTabs = new Map(state.tabs)

          if (wasRenamed) {
            newTabs.delete(tabId)
            newTabs.set(fileId, updatedTab)

            // Update tabOrder if it exists
            if (result.tabOrder) {
              const orderIndex = result.tabOrder.indexOf(tabId)
              if (orderIndex !== -1) {
                result.tabOrder[orderIndex] = fileId
              }
            }

            // Update activeTabId if this was the active tab
            if (result.activeTabId === tabId) {
              result.activeTabId = fileId
            }
          } else {
            newTabs.set(tabId, updatedTab)
          }

          result = {
            ...result,
            tabs: newTabs
          }

          this.scheduleSaveStatusHide(targetId)
        }

        // Update documents list in the same update
        if (wasRenamed) {
          // For renamed files, DON'T update documents here
          // It will be updated later via file-renamed event to avoid flicker
          // Just keep the existing documents array
          result.documents = state.documents
        } else {
          // Not renamed, just update existing document
          const existingIndex = state.documents.findIndex(doc => doc.id === fileId)

          if (existingIndex === -1) {
            // New document - add to list
            const newDoc: DocumentInfo = {
              id: fileId,
              title: tab.title,
              preview: tab.content.substring(0, 200).replace(/\n/g, ' ').trim(),
              createdAt: new Date(),
              updatedAt: new Date(),
              wordCount: tab.content.length,
              tags: tab.metadata?.tags
            }
            result.documents = [newDoc, ...state.documents]
          } else {
            // Existing document - update it
            const updatedDocs = [...state.documents]
            updatedDocs[existingIndex] = {
              ...updatedDocs[existingIndex],
              id: fileId,
              title: tab.title,
              preview: tab.content.substring(0, 200).replace(/\n/g, ' ').trim(),
              updatedAt: new Date(),
              wordCount: tab.content.length
            }
            result.documents = updatedDocs
          }
        }

        return result
      })
      
      // Trigger FileExplorer update in same microtask
      // Use queueMicrotask to ensure it runs after store update but before render
      if (typeof window !== 'undefined' && wasRenamed) {
        queueMicrotask(() => {
          // Remove .md extension for FileExplorer (its node IDs don't have extensions)
          const oldPathWithoutExt = originalRelativeFileId.endsWith('.md')
            ? originalRelativeFileId.slice(0, -3)
            : originalRelativeFileId
          const newPathWithoutExt = fileId.endsWith('.md')
            ? fileId.slice(0, -3)
            : fileId

          console.log('[fileStore] Dispatching file-renamed:', {
            oldPath: oldPathWithoutExt,
            newPath: newPathWithoutExt,
            title: tab.title
          })
          window.dispatchEvent(new CustomEvent('file-renamed', {
            detail: {
              oldPath: oldPathWithoutExt,
              newPath: newPathWithoutExt,
              newTitle: tab.title
            }
          }))
        })

        // Update documents after skipNextRebuild has been reset (after 200ms)
        // This ensures FileExplorer doesn't rebuild the tree
        setTimeout(() => {
          this.store.update(state => {
            const oldIndex = state.documents.findIndex(doc =>
              doc.id === originalRelativeFileId || doc.id === (originalRelativeFileId + '.md'))

            if (oldIndex !== -1) {
              const updatedDocs = [...state.documents]
              updatedDocs[oldIndex] = {
                ...updatedDocs[oldIndex],
                id: fileId,
                title: tab.title
              }
              return { ...state, documents: updatedDocs }
            }
            return state
          })
        }, 250) // After skipNextRebuild reset (200ms) + buffer
      }
    } catch (error) {
      console.error('Failed to save tab:', error)
      // Check if it's a permission error
      const errorMessage = (error as Error).message
      if (errorMessage && (errorMessage.includes('권한') || errorMessage.includes('Permission') || errorMessage.includes('permission'))) {
        // Show user-friendly message for permission errors
        const { errorService } = await import('$lib/services/ErrorService')
        errorService.showWarning('파일 저장 권한이 없습니다. Vault 폴더를 다시 선택하거나 Cmd/Ctrl+S로 수동 저장해주세요.')
        // Don't throw error for permission issues to prevent cascading failures
        return
      }
      throw error
    }
  }
  
  /**
   * Save all dirty tabs
   */
  async saveAllTabs(): Promise<void> {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('flush-editor-content'))
    }

    const state = get(this.store)
    const dirtyTabs = Array.from(state.tabs.values()).filter(tab => tab.isDirty)
    
    for (const tab of dirtyTabs) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('flush-editor-content', {
            detail: { tabId: tab.id }
          })
        )
      }
      await this.saveTab(tab.id)
    }
  }
  
  /**
   * Create new document
   */
  async createNewDocument(title?: string, folderPath?: string | null): Promise<string> {
    // Use current folder from state if not provided
    const state = get(this.store)

    // Get vault path to make folder paths relative
    const { vaultStore } = await import('./vaultStore')
    const vault = get(vaultStore)
    const vaultPath = vault?.vaultPath || ''

    // Defensive check: warn if vault path not available in Electron
    const isElectronCreate = typeof window !== 'undefined' && (window as any).api
    if (isElectronCreate && !vaultPath) {
      console.warn('[fileStore.createNewDocument] Vault path not available yet, folder path calculation may be inaccurate')
    }

    // If folderPath is explicitly null, use root
    // If folderPath is undefined, use default behavior (current folder or active tab's folder)
    // If folderPath is a string, use that specific folder
    let targetFolder: string | undefined
    if (folderPath === null) {
      // Explicitly request root folder
      targetFolder = undefined
    } else if (folderPath !== undefined) {
      // Specific folder provided
      targetFolder = folderPath
    } else {
      // Default behavior: try to use current folder or active tab's path
      targetFolder = state.currentFolder
      if (!targetFolder && state.activeTabId) {
        const activeTab = state.tabs.get(state.activeTabId)
        if (activeTab && activeTab.id.includes('/')) {
          // Extract folder from active tab's path
          const fullPath = activeTab.id.substring(0, activeTab.id.lastIndexOf('/'))

          // Make the folder path relative to vault path
          if (vaultPath && fullPath.startsWith(vaultPath)) {
            // Remove vault path and leading slash to get relative path
            targetFolder = fullPath.substring(vaultPath.length).replace(/^\//, '')
          } else {
            targetFolder = fullPath
          }
        }
      }
    }

    // Generate unique title within the target folder (undefined = root)
    const uniqueTitle = title || await this.generateUniqueTitle('Untitled', targetFolder)

    // Create actual file immediately with folder path
    const id = await storage.createDocument(uniqueTitle, '', targetFolder)
    
    // Mark as self-change to prevent external change detection
    try {
      const { getFileSystemWatcher } = await import('$lib/services/FileSystemWatcher')
      const fsWatcher = getFileSystemWatcher()
      
      // Ensure the path has .md extension for markSelfChange
      const filePathForWatcher = id.endsWith('.md') ? id : `${id}.md`
      
      if (fsWatcher && typeof fsWatcher.markSelfChange === 'function') {
        fsWatcher.markSelfChange(filePathForWatcher)
      }
    } catch (error) {
      console.error('[fileStore] Failed to mark self-change:', error)
    }
    
    // Load the created document
    const newDoc = await storage.load(id)
    
    // Add to documents list
    const docInfo: DocumentInfo = {
      id: newDoc.id,
      title: newDoc.title,
      preview: newDoc.content.substring(0, 200).replace(/\n/g, ' ').trim(),
      createdAt: newDoc.createdAt,
      updatedAt: newDoc.updatedAt,
      wordCount: newDoc.content.length,
      tags: newDoc.metadata?.tags
    }
    
    this.store.update(state => ({
      ...state,
      documents: [docInfo, ...state.documents]
    }))
    
    // Open the file as a tab
    await this.openFile(id)
    
    // File watcher will detect the new file automatically
    // Only dispatch manual refresh for non-Electron environments as fallback
    const isElectron = typeof window !== 'undefined' && (window as any).api?.watchFolder
    if (!isElectron && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('file-explorer-refresh'))
    }
    
    return id
  }
  
  /**
   * Delete document
   */
  async deleteDocument(id: string): Promise<void> {
    try {
      // Cancel auto-save timer if exists
      this.cancelAutoSave(id)
      
      // Remove tab without save confirmation if open
      const state = get(this.store)
      
      // Find tabs to close - normalize IDs the same way as openFile
      const tabsToClose = new Set<string>()
      
      // Normalize the ID for comparison (remove leading slashes, trailing .md)
      const normalizeId = (fileId: string) => {
        return fileId.replace(/^\/+/, '').replace(/\.md$/, '')
      }
      
      const normalizedRequestId = normalizeId(id)
      
      // Check all tabs with normalized comparison
      for (const [tabId, tab] of state.tabs) {
        const normalizedTabId = normalizeId(tabId)
        if (normalizedRequestId === normalizedTabId) {
          tabsToClose.add(tabId)
        }
      }
      
      // 경로가 다른 동명 파일은 닫지 않음 - 정확한 경로만 비교
      
      if (tabsToClose.size > 0) {
        // Remove tab(s) directly without save confirmation
        this.store.update(state => {
          const newTabs = new Map(state.tabs)
          
          // Delete all matching tabs
          for (const tabId of tabsToClose) {
            newTabs.delete(tabId)
            this.cancelAutoSave(tabId)
          }
          
          // If this was the active tab, activate another
          let newActiveId = state.activeTabId
          if (state.activeTabId && tabsToClose.has(state.activeTabId)) {
            const remainingTabs = Array.from(newTabs.keys())
            newActiveId = remainingTabs.length > 0 ? remainingTabs[0] : null
          }
          
          // Update tab order if it exists
          let newTabOrder = state.tabOrder
          if (state.tabOrder) {
            newTabOrder = state.tabOrder.filter(id => !tabsToClose.has(id))
          }
          
          
          return {
            ...state,
            tabs: newTabs,
            activeTabId: newActiveId,
            ...(newTabOrder ? { tabOrder: newTabOrder } : {})
          }
        })
      } else {
        // Also check for tabs that end with the same filename
        // This handles cases where paths might differ
        const filename = id.split('/').pop()
        if (filename) {
          const tabsToCloseList: string[] = []
          for (const [tabId, tab] of state.tabs) {
            // Check exact match
            if (tabId === id) {
              tabsToCloseList.push(tabId)
              continue
            }
            
            // Check if tab matches filename with or without .md
            const tabFilename = tabId.split('/').pop()
            if (tabFilename === filename) {
              tabsToCloseList.push(tabId)
              continue
            }
            
            // Check with/without .md extension
            if (filename.endsWith('.md')) {
              const filenameWithoutExt = filename.slice(0, -3)
              if (tabFilename === filenameWithoutExt || tabId.endsWith(`/${filenameWithoutExt}`)) {
                tabsToCloseList.push(tabId)
              }
            } else {
              const filenameWithExt = filename + '.md'
              if (tabFilename === filenameWithExt || tabId.endsWith(`/${filenameWithExt}`)) {
                tabsToCloseList.push(tabId)
              }
            }
          }
          
          if (tabsToCloseList.length > 0) {
            this.store.update(state => {
              const newTabs = new Map(state.tabs)
              tabsToCloseList.forEach(tabId => {
                this.cancelAutoSave(tabId)
                newTabs.delete(tabId)
              })
              
              // Update active tab if needed
              let newActiveId = state.activeTabId
              if (state.activeTabId && tabsToCloseList.includes(state.activeTabId)) {
                const remainingTabs = Array.from(newTabs.keys())
                newActiveId = remainingTabs.length > 0 ? remainingTabs[0] : null
              }
              
              // Update tab order if it exists
              let newTabOrder = state.tabOrder
              if (state.tabOrder) {
                newTabOrder = state.tabOrder.filter(id => !tabsToCloseList.includes(id))
              }
              
              return {
                ...state,
                tabs: newTabs,
                activeTabId: newActiveId,
                ...(newTabOrder ? { tabOrder: newTabOrder } : {})
              }
            })
          }
        }
      }
      
      // Delete from storage (all files now exist physically)
      await storage.delete(id)
      
      // Update the documents list - handle different id formats
      this.store.update(state => {
        // Normalize the id for comparison
        const normalizeId = (docId: string) => {
          // Remove .md extension if present
          let normalized = docId.endsWith('.md') ? docId.slice(0, -3) : docId
          // Get just the filename if it's a path
          if (normalized.includes('/')) {
            normalized = normalized.split('/').pop() || normalized
          }
          return normalized.toLowerCase()
        }
        
        const targetId = normalizeId(id)
        
        return {
          ...state,
          documents: state.documents.filter(doc => {
            const docNormalized = normalizeId(doc.id)
            const matches = docNormalized === targetId
            return !matches
          })
        }
      })
      
      // File watcher will detect deletion automatically
      // Only dispatch manual refresh for non-Electron environments
      const isElectron = typeof window !== 'undefined' && (window as any).api?.watchFolder
      if (!isElectron && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('file-explorer-refresh'))
      }
    } catch (error) {
      console.error('Failed to delete document:', error)
      // Reload documents to ensure consistency
      await this.loadDocuments()
    }
  }
  
  /**
   * Schedule auto-save for a tab
   */
  private async scheduleAutoSave(id: string) {
    // Cancel existing timer
    this.cancelAutoSave(id)
    
    // Get auto-save settings
    const { settingsStore } = await import('./settings')
    const settings = get(settingsStore)
    
    // Only schedule if auto-save is enabled
    if (!settings.editor?.autoSave) {
      return
    }
    
    // Use configured interval or default to 30 seconds
    const autoSaveInterval = settings.editor?.autoSaveInterval || 30000
    
    // Debounce: wait for user to stop typing
    // Use shorter debounce (5 seconds) for quick saves after typing stops
    const debounceDelay = Math.min(5000, autoSaveInterval / 2)
    
    // Schedule save after debounce delay
    const timer = setTimeout(async () => {
      try {
        const state = get(this.store)
        const tab = state.tabs.get(id)
        
        // Only save if tab still exists and is dirty
        if (tab && tab.isDirty) {
          await this.saveTab(id)
        }
      } catch (error) {
        // Silently handle permission errors during auto-save
        const errorMessage = (error as Error).message
        if (errorMessage && (errorMessage.includes('Permission denied') || errorMessage.includes('permission'))) {
          // Don't spam the console with permission errors
          return
        }
        console.error(`Failed to auto-save tab ${id}:`, error)
      }
    }, debounceDelay)
    
    this.autoSaveTimers.set(id, timer)
  }
  
  /**
   * Cancel auto-save timer
   */
  cancelAutoSave(id: string) {
    const timer = this.autoSaveTimers.get(id)
    if (timer) {
      clearTimeout(timer)
      this.autoSaveTimers.delete(id)
    }
  }

  /**
   * Schedule hiding save status message after a delay
   */
  private scheduleSaveStatusHide(id: string) {
    // Cancel existing timer if any
    const existingTimer = this.saveStatusTimers.get(id)
    if (existingTimer) {
      clearTimeout(existingTimer)
    }
    
    // Schedule hiding after 1.5 seconds
    const timer = setTimeout(() => {
      this.store.update(state => {
        const tab = state.tabs.get(id)
        if (tab) {
          state.tabs.set(id, {
            ...tab,
            showSaveStatus: false
          })
        }
        return {
          ...state,
          tabs: new Map(state.tabs)
        }
      })
      this.saveStatusTimers.delete(id)
    }, 1500)
    
    this.saveStatusTimers.set(id, timer)
  }
  
  /**
   * Get next/previous tab
   */
  getAdjacentTab(direction: 'next' | 'prev'): string | null {
    const state = get(this.store)
    const tabIds = Array.from(state.tabs.keys())
    
    if (tabIds.length === 0 || !state.activeTabId) return null
    
    const currentIndex = tabIds.indexOf(state.activeTabId)
    if (currentIndex === -1) return null
    
    if (direction === 'next') {
      return tabIds[(currentIndex + 1) % tabIds.length]
    } else {
      return tabIds[(currentIndex - 1 + tabIds.length) % tabIds.length]
    }
  }
  
  /**
   * Switch to next/previous tab
   */
  switchTab(direction: 'next' | 'prev') {
    const nextId = this.getAdjacentTab(direction)
    if (nextId) {
      this.setActiveTab(nextId) // async but we don't need to await here
    }
  }
  
  /**
   * Update tab ID when file is moved/renamed
   */
  updateTabId(oldId: string, newId: string): void {
    const state = get(this.store)
    const tab = state.tabs.get(oldId)
    
    if (tab) {
      // Remove old tab entry
      state.tabs.delete(oldId)
      
      // Update tab with new ID
      const updatedTab = {
        ...tab,
        id: newId,
        filePath: newId
      }
      
      // Add with new ID
      state.tabs.set(newId, updatedTab)
      
      // Update active tab ID if it was the active one
      if (state.activeTabId === oldId) {
        state.activeTabId = newId
      }
      
      // Update the store
      this.store.set(state)
    }
  }

  /**
   * Close all tabs
   */
  async closeAllTabs(): Promise<void> {
    // Cancel any in-flight enrichment when all tabs are closed (vault switch, etc.)
    this.enrichmentGeneration++

    const state = get(this.store)
    const tabIds = Array.from(state.tabs.keys())

    for (const id of tabIds) {
      const closed = await this.closeTab(id)
      if (!closed) break // User cancelled
    }
  }
  
  /**
   * Save current session state
   */
  saveSessionState() {
    const state = get(this.store)
    
    // Convert tab IDs to relative paths before saving
    const openTabIds = Array.from(state.tabs.keys()).map(tabId => {
      // Simple heuristic to detect absolute paths and convert to relative
      // Check if it's an absolute path (starts with / or contains drive letter)
      if (tabId.startsWith('/') || /^[A-Z]:/i.test(tabId)) {
        // Try to extract just the relative part
        // Look for common patterns in vault paths
        const match = tabId.match(/(?:Documents|Library|iCloud|vault|obsidian|korean-grammar)\/(.+)$/)
        if (match) {
          return match[1]
        }
        // Otherwise, try to get the last part after the last major directory
        const parts = tabId.split('/')
        if (parts.length > 2) {
          // Find index of common vault directory names
          const vaultDirIndex = parts.findIndex(part => 
            /^(Documents|vault|obsidian|korean-grammar|junlim|Desktop|Downloads)$/i.test(part)
          )
          if (vaultDirIndex >= 0 && vaultDirIndex < parts.length - 1) {
            // Return everything after the vault directory
            return parts.slice(vaultDirIndex + 1).join('/')
          }
        }
      }
      return tabId
    })
    
    localStorage.setItem('korean-grammar-open-tabs', JSON.stringify(openTabIds))
    
    // Convert active tab ID to relative path using same heuristic
    let activeTabId = state.activeTabId
    if (activeTabId) {
      if (activeTabId.startsWith('/') || /^[A-Z]:/i.test(activeTabId)) {
        const match = activeTabId.match(/(?:Documents|Library|iCloud|vault|obsidian|korean-grammar)\/(.+)$/)
        if (match) {
          activeTabId = match[1]
        } else {
          const parts = activeTabId.split('/')
          if (parts.length > 2) {
            const vaultDirIndex = parts.findIndex(part => 
              /^(Documents|vault|obsidian|korean-grammar|junlim|Desktop|Downloads)$/i.test(part)
            )
            if (vaultDirIndex >= 0 && vaultDirIndex < parts.length - 1) {
              activeTabId = parts.slice(vaultDirIndex + 1).join('/')
            }
          }
        }
      }
    }
    
    // Save active tab ID
    if (activeTabId) {
      localStorage.setItem('korean-grammar-active-tab', activeTabId)
    } else {
      localStorage.removeItem('korean-grammar-active-tab')
    }
  }
  
  /**
   * Cleanup
   */
  destroy() {
    // Save session state before cleanup
    this.saveSessionState()
    
    // Cancel all auto-save timers
    this.autoSaveTimers.forEach(timer => clearTimeout(timer))
    this.autoSaveTimers.clear()
    
    // Cancel all save status timers
    this.saveStatusTimers.forEach(timer => clearTimeout(timer))
    this.saveStatusTimers.clear()
  }
}

// Create singleton instance
export const fileStore = new FileStore()

// Debug access
if (typeof window !== 'undefined') {
  (window as any).__fileStore = fileStore
}

// Derived stores for convenient access
export const openTabs = derived(
  fileStore,
  $fileStore => Array.from($fileStore.tabs.values())
)

export const activeTab = derived(
  fileStore,
  $fileStore => $fileStore.activeTabId ? $fileStore.tabs.get($fileStore.activeTabId) : null
)

export const documents = derived(
  fileStore,
  $fileStore => $fileStore.documents
)

export const hasUnsavedChanges = derived(
  fileStore,
  $fileStore => Array.from($fileStore.tabs.values()).some(tab => tab.isDirty)
)

// Derived stores for undo/redo state of active tab
export const canUndoActiveTab = derived(
  [fileStore, activeTab],
  ([$fileStore, $activeTab]) => {
    if (!$activeTab) return false
    const tab = $fileStore.tabs.get($activeTab.id)
    return tab?.history ? tab.history.entries.length > 1 && tab.history.currentIndex > 0 : false
  }
)

export const canRedoActiveTab = derived(
  [fileStore, activeTab],
  ([$fileStore, $activeTab]) => {
    if (!$activeTab) return false
    const tab = $fileStore.tabs.get($activeTab.id)
    return tab?.history ? tab.history.currentIndex < tab.history.entries.length - 1 : false
  }
)
